import React from 'react';
import { Link } from 'react-router-dom';

const PLATFORMS = [
  {
    category: 'AI Engine', id: 'ai-engine',
    products: [
      { label: 'AI Engine', badge: 'progress', title: 'LENA Code®', tagline: 'Normalization Engine', body: 'The foundational AI layer of the LENA platform. LENA Code® normalizes raw DNA methylation data across Illumina array platforms (450K, EPIC v1.0, EPIC v2.0), reducing platform batch effects and technical noise to produce a standardized methylation signal suitable for downstream analysis. Patent filed.', status: 'Ready for Deployment' },
      { label: 'AI Engine', badge: 'progress', title: 'LENA Score™', tagline: 'Risk Scoring Algorithm', body: 'Converts normalized methylation signals into a validated quantitative patient risk score. Calibrated against the ALER-5 cohort dataset of 538,090 longitudinal observations from 199,800 patient EMRs. Outputs a single, clinician-readable score indicating pre-leukemic risk level.', status: 'Ready for Deployment' },
    ],
  },
  {
    category: 'Commercial Assays', id: 'commercial',
    products: [
      { label: 'Diagnostics', badge: 'active', title: 'LENA Q51®', tagline: 'Peripheral Blood Leukemia Detection', body: 'Alercell\'s flagship commercial diagnostic assay. Detects 51 gene-associated methylation signatures in peripheral blood indicative of pre-leukemic transformation. Currently available as a Laboratory Developed Test (LDT) through LenaDx. AUC 0.967 on the ALER-5 cohort. Revenue-generating today.', status: 'Commercial (LDT)' },
      { label: 'Diagnostics', badge: 'active', title: 'LENA S1®', tagline: 'Peripheral Blood Lung Cancer Detection', body: 'Peripheral blood methylation assay for early lung cancer detection. Uses the same LENA Code® normalization infrastructure as LENA Q51®, enabling multi-cancer screening from a single blood draw. Available as LDT through LenaDx.', status: 'Commercial (LDT)' },
    ],
  },
  {
    category: 'Next-Generation Pipeline', id: 'pipeline',
    products: [
      { label: 'Pipeline', badge: 'progress', title: 'LENA qMETHYL Series™', tagline: 'Multi-Gene Methylation Panels', body: 'Alercell\'s flagship next-generation diagnostic pipeline — a family of multi-gene methylation panels spanning rapid screening to comprehensive profiling. Individual panel names and gene targets are not publicly disclosed (IP protection). IFU documentation complete for all panels. Primary Series A revenue drivers following clinical validation. Patents ongoing under existing 24+ patent portfolio. Patent Filed.', status: 'IFU Complete / Patent Filed' },
    ],
  },
  {
    category: 'Epitranscriptomics', id: 'epitranscriptomics',
    products: [
      { label: 'First-in-Class', badge: 'progress', title: 'm5C-ESSENTIAL™', tagline: 'tRNA Methylation Assay', body: 'First-in-class commercial assay targeting 5-methylcytosine (m5C) modifications in transfer RNA — a biologically earlier and orthogonal signal to DNA methylation. Operates in entirely uncontested commercial space. IFU documentation complete. Patent filed.', status: 'IFU Complete — Patent Filed' },
      { label: 'First-in-Class', badge: 'progress', title: 'm6A-ESSENTIAL™', tagline: 'mRNA Methylation Assay', body: 'First-in-class commercial assay targeting N6-methyladenosine (m6A) modifications in messenger RNA — an epitranscriptomic layer governing post-transcriptional gene regulation and cancer progression. IFU documentation complete. Patent Filed. While the diagnostics industry has focused on DNA methylation, Alercell has developed the first commercial assays targeting RNA methylation.', status: 'IFU Complete / Patent Filed' },
    ],
  },
  {
    category: 'Theranostics', id: 'theranostics',
    products: [
      { label: 'Theranostics', badge: 'progress', title: 'LENA-Rx™', tagline: 'Theranostic Guidance Platform', body: 'Links LENA Score™ diagnostic output to treatment pathway selection. Integrates real-time literature search across PubMed and ClinicalTrials.gov for variant-specific leukemia treatment guidance. Provides theranostic decision support at the point of clinical decision-making.', status: 'Development Stage' },
      { label: 'Theranostics', badge: 'progress', title: 'LENA Epigenetic Clock™', tagline: 'Biological Age Acceleration', body: 'Measures biological age acceleration — the divergence between chronological age and epigenetic age — as a marker of pre-leukemic cellular stress. Enables longitudinal tracking of a patient\'s trajectory toward or away from transformation. Foundation of the Kinetic Information Theory framework.', status: 'Ready for Deployment' },
    ],
  },
  {
    category: 'Infrastructure', id: 'infrastructure',
    products: [
      { label: 'Platform', badge: 'active', title: 'Deep Leukemic Cloud®', tagline: 'Clinical Data Infrastructure', body: 'LENA Platform is a scalable, cloud-based infrastructure designed for the ingestion, processing, and clinical reporting of methylation data. It houses the ALER-5 dataset, comprising 538,090 longitudinal observations from 199,800 patient EMRs across five cohorts and three Illumina platforms. Currently accessible at www.LenaPlatform.com, the ecosystem is transitioning to a dedicated AWS environment to ensure full HIPAA and regulatory compliance.', status: 'Ready for Deployment' },
    ],
  },
];

export default function Platform() {
  return (
    <main>
      {/* Hero */}
      <section className="page-hero">
        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
          <span className="eyebrow">The LENA Platform Ecosystem</span>
          <h1 className="hero-headline" style={{ maxWidth: 780, marginBottom: 24 }}>
            Ten integrated platforms.<br />One epigenetic signal. One clinical mission.
          </h1>
          <p className="body-lg" style={{ maxWidth: 680 }}>
            The LENA platform suite is a fully integrated, multi-modal diagnostic and theranostic ecosystem spanning commercial assays, next-generation validation-stage diagnostics, and first-in-class RNA epitranscriptomic detection. Every product is built on the same validated epigenetic signal — compounding clinical value across the entire pipeline.
          </p>
        </div>
      </section>

      {/* Platform Video */}
      <section style={{ background: 'var(--bg-surface)', padding: '60px 0', borderBottom: '1px solid var(--border)' }}>
        <div className="container">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 60, alignItems: 'center' }} className="bio-grid">
            <div>
              <span className="eyebrow">Platform Overview</span>
              <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 28, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 16 }}>
                See the LENA platform in action.
              </h2>
              <p className="body-md" style={{ marginBottom: 16 }}>
                Watch how the LENA diagnostic ecosystem transforms raw epigenetic data from a standard blood draw into a validated, quantitative pre-leukemic risk score — validated across 200,000+ patient records.
              </p>
              <div style={{ display: 'flex', gap: 24, marginTop: 24 }}>
                {[
                  { value: '200K+', label: 'Patient records validated' },
                  { value: 'AUC 0.967', label: 'Retrospective discrimination (internal)' },
                ].map((s, i) => (
                  <div key={i}>
                    <span style={{ fontFamily: 'var(--font-display)', fontSize: 24, fontWeight: 800, color: 'var(--accent-cyan)', display: 'block' }}>{s.value}</span>
                    <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{s.label}</span>
                  </div>
                ))}
              </div>
            </div>
            <div style={{ borderRadius: 'var(--radius)', overflow: 'hidden', border: '1px solid var(--border)', aspectRatio: '16/9' }}>
              <iframe
                width="100%" height="100%"
                src="https://www.youtube.com/embed/JyzAPeGwY2s"
                title="Alercell LENA Platform Overview"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                style={{ display: 'block' }}
              />
            </div>
          </div>
        </div>
      </section>
      <div className="metric-strip">
        <div className="container">
          <div className="metric-grid">
            {[
              { value: '538,090', label: 'Longitudinal methylation observations (ALER-5 cohort)' },
              { value: '199,800', label: 'De-identified patient EMRs across 5 independent cohorts' },
              { value: '3', label: 'Illumina array platforms (450K, EPIC v1.0, EPIC v2.0)' },
              { value: '24+', label: 'Patent applications in portfolio' },
            ].map((m, i) => (
              <div key={i} className="metric-item">
                <span className="metric-value">{m.value}</span>
                <p className="metric-label">{m.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Category Nav */}
      <div style={{ background: 'var(--bg-surface)', borderBottom: '1px solid var(--border)', padding: '20px 0', position: 'sticky', top: 72, zIndex: 100 }}>
        <div className="container" style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 4 }}>
          {PLATFORMS.map(cat => (
            <a key={cat.id} href={`#${cat.id}`} style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--accent-cyan)', textTransform: 'uppercase', letterSpacing: '0.1em', textDecoration: 'none', padding: '6px 14px', border: '1px solid var(--accent-cyan-border)', borderRadius: 20, whiteSpace: 'nowrap', transition: 'all 0.2s' }}
              onMouseEnter={e => { e.target.style.background = 'var(--accent-cyan-dim)'; }}
              onMouseLeave={e => { e.target.style.background = 'transparent'; }}
            >{cat.category}</a>
          ))}
        </div>
      </div>

      {/* Pipeline Overview Table */}
      <section className="section section-surface">
        <div className="container">
          <span className="eyebrow">Development Pipeline</span>
          <h2 className="section-headline" style={{ marginBottom: 8 }}>Platform status overview</h2>
          <div className="cyan-line" />
          <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'hidden' }}>
            <div className="pipeline-row" style={{ background: 'rgba(0,174,239,0.05)', borderBottom: '1px solid var(--border-cyan)' }}>
              <span>Platform</span><span>Category</span><span>Description</span><span>Stage</span>
            </div>
            {[
              ['LENA Code®', 'AI Engine', 'Methylation normalization engine', 'Ready for Deployment'],
              ['LENA Score™', 'AI Engine', 'Risk scoring algorithm', 'Ready for Deployment'],
              ['LENA Q51®', 'Commercial', 'Leukemia detection assay (51 genes)', 'Commercial LDT'],
              ['LENA S1®', 'Commercial', 'Lung cancer detection assay', 'Commercial LDT'],
              ['LENA qMETHYL Series™', 'Next-Gen', 'Multi-gene methylation panel family', 'IFU Complete / Patent Filed'],
              ['m5C-ESSENTIAL™', 'Epitranscriptomics', 'tRNA m5C methylation assay', 'IFU Complete / Patent Filed'],
              ['m6A-ESSENTIAL™', 'Epitranscriptomics', 'mRNA m6A methylation assay', 'IFU Complete / Patent Filed'],
              ['LENA-Rx™', 'Theranostics', 'Theranostic guidance platform', 'Development'],
              ['LENA Epigenetic Clock™', 'Theranostics', 'Biological age acceleration', 'Ready for Deployment'],
              ['Deep Leukemic Cloud®', 'Infrastructure', 'Clinical data platform (ALER-5)', 'Ready for Deployment'],
            ].map(([name, cat, desc, stage], i) => (
              <div key={i} className="pipeline-row">
                <span style={{ fontFamily: 'var(--font-display)', fontWeight: 600, color: 'var(--text-primary)', fontSize: 13 }}>{name}</span>
                <span><span className="badge badge-active" style={{ fontSize: 10 }}>{cat}</span></span>
                <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{desc}</span>
                <span><span className={`badge ${stage.includes('Deployed') || stage.includes('Commercial') ? 'badge-active' : stage.includes('IFU') ? 'badge-complete' : 'badge-progress'}`} style={{ fontSize: 10 }}>{stage}</span></span>
              </div>
            ))}
          </div>
          <style>{`@media(max-width:768px){.pipeline-row{grid-template-columns:1fr 1fr!important;}}`}</style>
        </div>
      </section>

      {/* Platform Detail Sections */}
      {PLATFORMS.map((cat, ci) => (
        <section key={cat.id} id={cat.id} className={`section ${ci % 2 === 0 ? '' : 'section-surface'}`}>
          <div className="container">
            <span className="eyebrow">{cat.category}</span>
            <div className="platform-grid">
              {cat.products.map((p, i) => (
                <div key={i} className="card" style={{ borderTop: `3px solid var(--accent-cyan)` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                    <span className="card-label">{p.label}</span>
                    <span className={`badge ${p.badge === 'active' ? 'badge-active' : p.badge === 'progress' ? 'badge-progress' : 'badge-complete'}`}>{p.status}</span>
                  </div>
                  <h3 className="card-title" style={{ marginBottom: 4 }}>{p.title}</h3>
                  <p style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-muted)', marginBottom: 16 }}>{p.tagline}</p>
                  <p className="body-md">{p.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      ))}

      {/* Regulatory */}
      <section className="section section-surface" id="regulatory">
        <div className="container">
          <span className="eyebrow">Regulatory Strategy</span>
          <h2 className="section-headline" style={{ marginBottom: 8 }}>A phased pathway from LDT to IVD clearance.</h2>
          <div className="cyan-line" />
          <div className="stat-grid">
            {[
              { title: 'Current: LDT / CLIA via LenaDx', body: 'LENA Q51® and LENA S1® are currently available as Laboratory Developed Tests (LDTs) through LenaDx. LDT designation under CLIA allows a certified laboratory to develop, validate, and offer tests without premarket FDA clearance.' },
              { title: 'IVD Regulatory Pathway', body: 'The next-generation qMETHYL Series™ platforms are being designed with De Novo 510(k) submission in mind, enabling broader commercial deployment through third-party laboratories beyond the LenaDx network. The company is expecting to make it available via LDT in the coming weeks.' },
              { title: 'Key Milestones', body: 'Q3 2026: CLIA Lab Launch. Q1 2027: 15 Pilot Sites. Q2 2027: Payer Dossier (CMS & private). Q4 2027: De Novo submission package for LENA Q51® ready.' },
            ].map((item, i) => (
              <div key={i} style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 32 }}>
                <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 12 }}>{item.title}</h3>
                <p className="body-md">{item.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section" style={{ background: 'var(--bg-primary)' }}>
        <div className="container cta-banner">
          <h2 className="section-headline">Ready to go deeper?</h2>
          <p className="body-lg">Review the full validation dataset, AI methodology, and peer-reviewed publications.</p>
          <div className="cta-buttons">
            <Link to="/science#validation" className="btn btn-primary">View Validation Data</Link>
            <Link to="/contact" className="btn btn-outline">Request White Paper</Link>
            <Link to="/investors" className="btn btn-text">Investor Materials</Link>
          </div>
        </div>
      </section>
    </main>
  );
}
